// Component to display citations at the end of messages
import React, { useState, useRef, useEffect } from 'react';

const CitationsDisplay = ({ citations }) => {
    // Return null if no citations
    if (!citations || citations.length === 0) {
        return null;
    }

    // Ref for horizontal scrolling
    const scrollContainerRef = useRef(null);

    // State for scroll position tracking
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    // Function to extract domain from URL
    const getDomain = (url) => {
        try {
            const urlObj = new URL(url);
            return urlObj.hostname.replace('www.', '');
        } catch (error) {
            return url;
        }
    };

    // Function to get favicon URL for a domain
    const getFaviconUrl = (url) => {
        try {
            const urlObj = new URL(url);
            return `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=32`;
        } catch (error) {
            return null;
        }
    };

    // Function to truncate title if too long
    const truncateTitle = (title, maxLength = 50) => {
        if (!title || title.length <= maxLength) return title;
        return title.substring(0, maxLength) + '...';
    };

    // Function to truncate description
    const truncateDescription = (description, maxLength = 80) => {
        if (!description || description.length <= maxLength) return description;
        return description.substring(0, maxLength) + '...';
    };

    // Custom external link icon component
    const ExternalLinkIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24">
            <path fill="currentColor" d="m16.289 7.208l-9.766 9.746q-.14.14-.344.13q-.204-.009-.345-.15t-.14-.334t.14-.334L15.582 6.5H6.789q-.213 0-.357-.144t-.143-.357t.143-.356t.357-.143h9.692q.343 0 .575.232t.233.576V16q0 .213-.145.356t-.356.144t-.356-.144t-.144-.356z" />
        </svg>
    );

    // Favicon component with fallback
    const FaviconImage = ({ url, domain }) => {
        const [hasError, setHasError] = useState(false);
        const faviconUrl = getFaviconUrl(url);

        if (hasError || !faviconUrl) {
            // Fallback to a simple globe icon
            return (
                <div className="w-4 h-4 rounded-sm bg-bg-30 flex items-center justify-center">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                        <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" stroke="currentColor" strokeWidth="2" />
                    </svg>
                </div>
            );
        }

        return (
            <img
                src={faviconUrl}
                alt={`${domain} favicon`}
                className="w-4 h-4 rounded-sm"
                onError={() => setHasError(true)}
            />
        );
    };

    // Function to scroll left
    const scrollLeft = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
        }
    };

    // Function to scroll right
    const scrollRight = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
        }
    };

    // Function to check scroll position and update arrow visibility
    const checkScrollPosition = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
        }
    };

    // Effect to check scroll position on mount and when citations change
    useEffect(() => {
        checkScrollPosition();

        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', checkScrollPosition);
            // Also check on resize
            window.addEventListener('resize', checkScrollPosition);

            return () => {
                container.removeEventListener('scroll', checkScrollPosition);
                window.removeEventListener('resize', checkScrollPosition);
            };
        }
    }, [citations]);

    return (
        <div
            className="mt-2 pt-1.5 border-t border-bd-50 max-w-3xl mx-auto"
            onMouseEnter={(e) => e.stopPropagation()}
            onMouseLeave={(e) => e.stopPropagation()}
        >
            {/* Citations header with count first, then favicons */}
            <div className="flex items-center gap-2 mb-1.5">
                <span className="text-sm font-medium text-tx-02">
                    {citations.length} Citation{citations.length > 1 ? 's' : ''}
                </span>
                <div className="flex items-center gap-1">
                    {citations.slice(0, 4).map((citation, index) => (
                        <FaviconImage
                            key={index}
                            url={citation.url}
                            domain={getDomain(citation.url)}
                        />
                    ))}
                    {citations.length > 4 && (
                        <span className="text-xs text-tx-03 ml-1">+{citations.length - 4}</span>
                    )}
                </div>
            </div>

            {/* Horizontal scrollable citations list */}
            <div className="relative overflow-hidden">
                {/* Left arrow - only show when can scroll left */}
                {canScrollLeft && (
                    <button
                        onClick={scrollLeft}
                        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-6 h-6 bg-bg-50 hover:bg-bg-40 rounded-full flex items-center justify-center shadow-sm border border-bd-50 opacity-80 hover:opacity-100 transition-all"
                    >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                )}

                {/* Citations container */}
                <div
                    ref={scrollContainerRef}
                    className="flex gap-1.5 overflow-x-auto scrollbar-hide"
                    style={{
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        paddingLeft: canScrollLeft ? '32px' : '0px',
                        paddingRight: canScrollRight ? '32px' : '0px'
                    }}
                >
                    {citations.map((citation, index) => (
                        <div
                            key={index}
                            data-citation-index={index}
                            className="group flex-shrink-0 w-60 bg-bg-50 hover:bg-bg-40 rounded-lg p-2 px-4 transition-all duration-200 cursor-pointer"
                            onClick={() => window.open(citation.url, '_blank', 'noopener,noreferrer')}
                            onMouseEnter={(e) => e.stopPropagation()}
                            onMouseLeave={(e) => e.stopPropagation()}
                        >
                            {/* Citation number inside card */}
                            <div className="flex items-start justify-between gap-2 mb-1.5">
                                <span className="text-[10px] font-medium text-tx-02 bg-bg-50 px-1.5 py-0.5 rounded">
                                    {index + 1}
                                </span>
                                <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <ExternalLinkIcon />
                                </div>
                            </div>

                            {/* Citation content */}
                            <div className="space-y-1.5">
                                {/* Citation title */}
                                <h4 className="text-sm font-medium text-fg-50 group-hover:text-ac-02 transition-colors line-clamp-2">
                                    {truncateTitle(citation.title || 'Untitled')}
                                </h4>

                                {/* Citation description */}
                                {citation.description && (
                                    <p className="text-xs text-tx-03 line-clamp-2">
                                        {truncateDescription(citation.description)}
                                    </p>
                                )}

                                {/* Citation domain with favicon */}
                                <div className="flex items-center gap-1 text-xs text-fg-30">
                                    <FaviconImage url={citation.url} domain={getDomain(citation.url)} />
                                    <span className="truncate">{getDomain(citation.url)}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Right arrow - only show when can scroll right */}
                {canScrollRight && (
                    <button
                        onClick={scrollRight}
                        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-6 h-6 bg-bg-50 hover:bg-bg-40 rounded-full flex items-center justify-center shadow-sm border border-bd-50 opacity-80 hover:opacity-100 transition-all"
                    >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                )}
            </div>
        </div>
    );
};

export default CitationsDisplay;